import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

import TeamManagement    from '../components/enterprise/TeamManagement';
import ApiKeyManager     from '../components/enterprise/ApiKeyManager';
import SubscriptionManager from '../components/enterprise/SubscriptionManager';
import RolePermissions   from '../components/enterprise/RolePermissions';
import AuditLogs         from '../components/enterprise/AuditLogs';
import WhiteLabelSettings from '../components/enterprise/WhiteLabelSettings';
import InvitationManager from '../components/enterprise/InvitationManager';
import AnalyticsDashboard from '../components/enterprise/AnalyticsDashboard';
import TenantSwitcher    from '../components/enterprise/TenantSwitcher';

const ROLE_TABS = {
  business_admin: ['Overview','Teams','Members','API Keys','Subscription','Roles','White Label','Analytics','Audit Logs'],
  team_manager:   ['Overview','Teams','Members','Analytics'],
  agent:          ['Overview','Teams'],
  api_partner:    ['Overview','API Keys'],
};

export default function TenantDashboardPage() {
  const { user, logout } = useAuth();
  const { activeTenant, tenantRole, clearTenant } = useTenant();
  const navigate = useNavigate();
  const [tab, setTab]     = useState('Overview');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading]   = useState(true);

  const tabs = ROLE_TABS[tenantRole] || ['Overview'];

  const loadOverview = useCallback(async () => {
    if (!activeTenant) return;
    setLoading(true);
    try {
      const res = await api.get('/tenant/analytics/overview');
      setOverview(res.data);
    } catch { /* analytics may not be available on free plan */ }
    finally { setLoading(false); }
  }, [activeTenant]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { if (!activeTenant) navigate('/onboarding'); }, [activeTenant, navigate]);

  const S = {
    page: { minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' },
    header: { background: 'var(--surface)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, padding: '0 20px', height: 56 },
    roleBadge: (role) => ({
      business_admin: { bg: '#5865f222', color: '#5865f2' },
      team_manager:   { bg: '#fab38722', color: '#fab387' },
      agent:          { bg: '#a6e3a122', color: '#a6e3a1' },
      api_partner:    { bg: '#89b4fa22', color: '#89b4fa' },
    }[role] || { bg: 'var(--bg)', color: 'var(--text-2)' }),
    body:   { flex: 1, padding: 24 },
    tabs:   { display: 'flex', gap: 4, marginBottom: 24, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 4, flexWrap: 'wrap' },
    tab:    (a) => ({ padding: '7px 14px', borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: 'pointer', border: 'none', background: a ? 'var(--accent)' : 'transparent', color: a ? '#fff' : 'var(--text-2)' }),
    grid4:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 16, marginBottom: 24 },
    stat:   { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 },
    statV:  { fontSize: 26, fontWeight: 700, marginBottom: 4 },
    statL:  { fontSize: 12, color: 'var(--text-3)' },
  };

  const rbadge = S.roleBadge(tenantRole);

  return (
    <div style={S.page}>
      <div style={S.header}>
        {activeTenant?.logo_url
          ? <img src={activeTenant.logo_url} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover' }} />
          : <div style={{ width: 28, height: 28, borderRadius: 7, background: 'linear-gradient(135deg,var(--accent),#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff' }}>{activeTenant?.name?.[0]}</div>
        }
        <span style={{ fontWeight: 700, fontSize: 14 }}>{activeTenant?.name}</span>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, letterSpacing: 1, background: rbadge.bg, color: rbadge.color }}>{tenantRole?.replace('_', ' ').toUpperCase()}</span>
        <div style={{ flex: 1 }} />
        <TenantSwitcher />
        <button onClick={() => navigate('/')} className="btn btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }}>API Workspace</button>
        <button onClick={() => { logout(); clearTenant(); navigate('/login'); }} className="btn btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }}>Sign out</button>
      </div>

      <div style={S.body}>
        <div style={S.tabs}>
          {tabs.map(t => <button key={t} style={S.tab(tab === t)} onClick={() => setTab(t)}>{t}</button>)}
        </div>

        {tab === 'Overview' && (
          <>
            <div style={S.grid4}>
              {overview ? [
                { label: 'API Endpoints',  val: overview.total_endpoints,  icon: '⚡' },
                { label: 'Total API Calls', val: overview.total_api_calls?.toLocaleString(), icon: '📡' },
                { label: 'Team Members',   val: overview.total_members,    icon: '👥' },
                { label: 'Teams',          val: overview.total_teams,      icon: '🏷️' },
              ].map(m => (
                <div key={m.label} style={S.stat}>
                  <div style={{ fontSize: 20, marginBottom: 8 }}>{m.icon}</div>
                  <div style={S.statV}>{m.val ?? '—'}</div>
                  <div style={S.statL}>{m.label}</div>
                </div>
              )) : (
                <div style={{ ...S.stat, gridColumn: '1/-1', color: 'var(--text-3)', fontSize: 13 }}>
                  Analytics available on Starter plan and above.
                  <button onClick={() => setTab('Subscription')} style={{ marginLeft: 8, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>Upgrade →</button>
                </div>
              )}
            </div>
            {overview?.recent_activity?.length > 0 && (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Recent Activity</h3>
                {overview.recent_activity.map((a, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                    <span><strong>{a.actor}</strong> — <code style={{ fontSize: 11 }}>{a.action}</code></span>
                    <span style={{ color: 'var(--text-3)' }}>{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'Teams'        && <TeamManagement />}
        {tab === 'Members'      && <InvitationManager />}
        {tab === 'API Keys'     && <ApiKeyManager />}
        {tab === 'Subscription' && <SubscriptionManager />}
        {tab === 'Roles'        && <RolePermissions />}
        {tab === 'White Label'  && <WhiteLabelSettings />}
        {tab === 'Analytics'    && <AnalyticsDashboard />}
        {tab === 'Audit Logs'   && <AuditLogs />}
      </div>
    </div>
  );
}
